import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to get query parameters from the current URL
 * @returns {Object} Object containing helper functions for query parameters
 */
export default function useQueryParams() {
  const location = useLocation();

  const queryParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const params = {};

    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }

    return params;
  }, [location.search]);

  /**
   * Get a specific query parameter value
   * @param {string} paramName - The name of the query parameter to retrieve
   * @param {string} [defaultValue=''] - Default value to return if parameter is not found
   * @returns {string} The query parameter value or default value
   */
  const getParam = (paramName, defaultValue = '') => {
    return queryParams[paramName] || defaultValue;
  };

  /**
   * Check if a query parameter exists
   * @param {string} paramName - The name of the query parameter to check
   * @returns {boolean} True if parameter exists, false otherwise
   */
  const hasParam = paramName => {
    return paramName in queryParams;
  };

  return {
    queryParams,
    getParam,
    hasParam
  };
}
